/**
 * 瀏覽器宿主的交換格式匯入匯出（MSPDI／XLSX／CSV）。
 *
 * 只做「選檔／下載 + store」的膠水：MSPDI 的轉換在 `@bigint-project/core`，
 * XLSX 與 CSV 在 `integration/` 的純函式模組。刻意不重用
 * `integration/*-service.ts`——那三個檔案靜態 import `@tauri-apps`，
 * 進了 web bundle 會被 `ops/scripts/check-web-bundle.mjs` 擋下。
 */

import { exportMspdi, importMspdi } from '@bigint-project/core';

import { exportProjectCsv, suggestedProjectCsvFilename } from '../../integration/csv-export';
import { assertMspdiImportable } from '../../integration/mspdi-diagnostics';
import { useProjectStore } from '../../store/project-store';
import type { AppPlatform } from '../ports';
import {
  createBrowserExchangeFileGateway,
  DEFAULT_MAX_PROJECT_BYTES,
  readBinaryFile,
  readProjectText,
  type BrowserExchangeFileGateway,
} from './file-api';

const MSPDI_ACCEPT = '.xml';
const MSPDI_EXTENSION = '.xml';
const MSPDI_MIME_TYPE = 'application/xml';
const XLSX_ACCEPT = '.xlsx';
const XLSX_EXTENSION = '.xlsx';
const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const CSV_EXTENSION = '.csv';
const CSV_MIME_TYPE = 'text/csv;charset=utf-8';

/** 交換格式那六個選用方法，在瀏覽器宿主一律提供。 */
export type BrowserExchangePlatform = Required<
  Pick<
    AppPlatform,
    'importMspdi' | 'exportMspdi' | 'importXlsx' | 'exportXlsx' | 'exportWbsXlsx' | 'exportCsv'
  >
>;

export interface BrowserExchangeOptions {
  readonly files?: BrowserExchangeFileGateway;
  /** 匯入失敗與匯入提示的呈現方式；預設用瀏覽器內建對話框。 */
  readonly notify?: (title: string, detail: string) => void;
  readonly maxImportBytes?: number;
  /**
   * MSPDI 匯入前的未儲存變更確認，比照 desktop 的 `confirmDiscardChanges`。
   * 只有 MSPDI 用得到：XLSX 走匯入精靈，取代動作在精靈裡另有確認，這裡再問
   * 一次會變成連跳兩個對話框。
   */
  readonly confirmDiscard?: () => boolean | Promise<boolean>;
}

function defaultNotify(title: string, detail: string): void {
  window.alert(`${title}\n\n${detail}`);
}

export function createBrowserExchangePlatform(
  options: BrowserExchangeOptions = {},
): BrowserExchangePlatform {
  const files = options.files ?? createBrowserExchangeFileGateway();
  const maxImportBytes = options.maxImportBytes ?? DEFAULT_MAX_PROJECT_BYTES;
  const notify = options.notify ?? defaultNotify;
  const confirmDiscard = options.confirmDiscard ?? (() => true);

  const report = (title: string, error: unknown): void => {
    const detail = error instanceof Error ? error.message : String(error);
    // eslint-disable-next-line no-console
    console.error(`[exchange] ${title}:`, error);
    notify(title, detail);
  };

  const currentProject = () => useProjectStore.getState().project;

  return {
    async importMspdi() {
      try {
        if (!(await confirmDiscard())) return;

        const file = await files.pickFile(MSPDI_ACCEPT);
        if (file === null) return;

        const xml = await readProjectText(file, maxImportBytes);
        const result = importMspdi(xml);
        assertMspdiImportable(result);
        // filePath 保持 null：匯入來源是 .xml，不能被當成 .bproj 的存檔目標。
        useProjectStore.getState().loadProject(result.project, null);
        useProjectStore.getState().runScheduleNow();
        useProjectStore.getState().setDirty(true);
        if (result.project.name.length === 0) {
          useProjectStore.getState().updateProjectMeta({ name: file.name });
        }
        if (result.diagnostics.length > 0) {
          notify('匯入 MSPDI 完成', `匯入成功，但有 ${result.diagnostics.length} 則提示，請檢查資料。`);
        }
      } catch (error) {
        report('匯入 MSPDI 失敗', error);
      }
    },

    async exportMspdi() {
      try {
        await files.downloadTextFile({
          contents: exportMspdi(currentProject()),
          suggestedName: 'project',
          extension: MSPDI_EXTENSION,
          mimeType: MSPDI_MIME_TYPE,
        });
      } catch (error) {
        report('匯出 MSPDI 失敗', error);
      }
    },

    async importXlsx() {
      try {
        const file = await files.pickFile(XLSX_ACCEPT);
        if (file === null) return;

        const bytes = await readBinaryFile(file, maxImportBytes);
        const { parseXlsxWorkbook } = await import('../../integration/xlsx-codec');
        useProjectStore.getState().openXlsxImport(parseXlsxWorkbook(bytes));
      } catch (error) {
        report('匯入 XLSX 失敗', error);
      }
    },

    async exportXlsx() {
      try {
        const project = currentProject();
        const { exportProjectXlsx, suggestedProjectXlsxFilename } = await import(
          '../../integration/xlsx-codec'
        );
        await files.downloadBinaryFile({
          contents: exportProjectXlsx(project),
          suggestedName: suggestedProjectXlsxFilename(project),
          extension: XLSX_EXTENSION,
          mimeType: XLSX_MIME_TYPE,
        });
      } catch (error) {
        report('匯出 XLSX 失敗', error);
      }
    },

    async exportWbsXlsx() {
      try {
        const project = currentProject();
        const { exportWbsXlsx, suggestedWbsXlsxFilename } = await import(
          '../../integration/xlsx-codec'
        );
        await files.downloadBinaryFile({
          contents: exportWbsXlsx(project),
          suggestedName: suggestedWbsXlsxFilename(project),
          extension: XLSX_EXTENSION,
          mimeType: XLSX_MIME_TYPE,
        });
      } catch (error) {
        report('匯出 WBS 清單失敗', error);
      }
    },

    async exportCsv() {
      try {
        const project = currentProject();
        await files.downloadTextFile({
          contents: exportProjectCsv(project),
          suggestedName: suggestedProjectCsvFilename(project),
          extension: CSV_EXTENSION,
          mimeType: CSV_MIME_TYPE,
        });
      } catch (error) {
        report('匯出 CSV 失敗', error);
      }
    },
  };
}
